// Armador de datos + render/descarga del AUTO INHIBITORIO (Art. 209) desde la
// plantilla oficial .docx (F-_AUTO_INHIBITORIO). Prellena con los datos ya
// recopilados del expediente; el contratista edita y previsualiza antes de descargar.
//
// Firmas (imagen real, módulo docxtemplater-image-module-free) — acumulativas según
// avanza el trámite de la decisión inicial:
//   - Elaboró           → contratista/tecnólogo que propuso  (decision.propuesta_por)
//   - Bajo COMUNÍQUESE  → abogado especializado asignado      (decision.revisor_abogado_id),
//                          aparece cuando se dio el visto bueno
//   - Debajo            → director que aprobó                 (decision.aprobada_por),
//                          aparece al aprobar
// La firma es la imagen que cada usuario cargó en «Mi firma»; si no tiene, línea en blanco.
import type { DecisionInicialResumen, ExpedienteDetalle } from '@/lib/expedientes';
import type { Firmante } from '@/lib/preRadicacion';
import { labelRol } from '@/lib/roles';
import { descargarBlob } from '@/lib/api';
import { cargarPlantilla, MIME_DOCX, PlantillaFaltanteError } from './motor';
import { PLANTILLAS } from './registro';
import { fechaEnLetras } from './fechas';

const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

const hoyISO = () => new Date().toISOString().slice(0, 10);

/** "1 de julio de 2026" a partir de un ISO. Vacío si no hay fecha. */
function fechaCorta(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso);
  if (isNaN(d.getTime())) return '';
  return `${d.getDate()} de ${MESES[d.getMonth()]} de ${d.getFullYear()}`;
}

export interface CampoAuto {
  key: string;
  label: string;
  multiline?: boolean;
}

// Campos editables del Auto Inhibitorio (en el orden del documento). Las firmas
// (Director / Elaboró) YA NO se escriben a mano: se toman de quien firma en el flujo.
export const CAMPOS_AUTO_INHIBITORIO: CampoAuto[] = [
  { key: 'RADICADO', label: 'Radicado / Proceso No.' },
  { key: 'IMPLICADO', label: 'Implicado' },
  { key: 'CARGO_ENTIDAD', label: 'Cargo y entidad' },
  { key: 'QUEJOSO', label: 'Informe o quejoso' },
  { key: 'FECHA_QUEJA', label: 'Fecha de la queja / informe' },
  { key: 'FECHA_HECHOS', label: 'Fecha de los hechos' },
  { key: 'CIUDAD', label: 'Ciudad' },
  { key: 'FECHA_LETRAS', label: 'Fecha del auto (en letras)' },
  { key: 'FOLIOS', label: 'Folios' },
  { key: 'ANTECEDENTES', label: 'Antecedentes', multiline: true },
  { key: 'MOTIVACION', label: 'Motivación (por qué es improcedente iniciar)', multiline: true },
  { key: 'CONCLUSION', label: 'Conclusión de las consideraciones', multiline: true },
];

export type DatosAutoInhibitorio = Record<string, string>;

/** Prellena los campos del auto con los datos ya capturados del expediente. */
export function prefillAutoInhibitorio(exp: ExpedienteDetalle): DatosAutoInhibitorio {
  const inv = exp.investigados?.[0];
  const quej = exp.quejosos?.[0];
  const hecho = exp.hechos?.[0];
  const radicado = exp.radicado_manual || exp.radicado || '';
  const implicado =
    inv?.nombre || (inv?.tipo === 'POR_ESTABLECER' ? 'Por individualizar' : 'Por determinar');
  const cargoEnt =
    [inv?.cargo, inv?.entidad_nombre].filter(Boolean).join(' / ') || 'Por determinar';
  const quejoso = quej?.nombre || 'Anónimo';
  const antecedentes =
    `En atención a la notificación disciplinaria radicada bajo el No. ${radicado}` +
    (quej?.nombre ? `, presentada por ${quej.nombre}` : '') +
    (hecho?.descripcion ? `, en la que se informa: ${hecho.descripcion}` : '') +
    '.';

  return {
    RADICADO: radicado,
    IMPLICADO: implicado,
    CARGO_ENTIDAD: cargoEnt,
    QUEJOSO: quejoso,
    FECHA_QUEJA: fechaCorta(exp.fecha_queja),
    FECHA_HECHOS: fechaCorta(hecho?.fecha_hechos),
    CIUDAD: 'Pereira',
    FECHA_LETRAS: fechaEnLetras(hoyISO()),
    FOLIOS: '',
    ANTECEDENTES: antecedentes,
    MOTIVACION: '',
    CONCLUSION: '',
  };
}

// ─────────────────────────── Firmas ───────────────────────────

// PNG 1x1 transparente: línea en blanco cuando aún no firma / no tiene firma cargada.
const PNG_TRANSPARENTE = Uint8Array.from(atob(
  'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNk+M8AAAMBAQDJ/pLvAAAAAElFTkSuQmCC',
), (c) => c.charCodeAt(0));

interface FirmanteRef { nombre: string; rol: string; firmaKey?: string | null; }

/** Resuelve los tres firmantes del auto según el estado de la decisión (acumulativo). */
function firmantesAuto(
  decision: DecisionInicialResumen | null | undefined,
  firmantes: Firmante[],
): { elaboro: FirmanteRef | null; revisor: FirmanteRef | null; director: FirmanteRef | null } {
  const buscar = (id?: string | null): FirmanteRef | null => {
    if (!id) return null;
    const f = firmantes.find((x) => x.id === id);
    return f ? { nombre: f.nombre, rol: f.rol, firmaKey: f.firmaKey } : null;
  };
  const estado = decision?.estado;
  const vistoBuenoDado =
    !!decision?.visto_bueno_por || estado === 'PENDIENTE_DIRECTOR' || estado === 'APROBADA';
  const aprobado = !!decision?.aprobada_por || estado === 'APROBADA';
  return {
    elaboro: buscar(decision?.propuesta_por),
    // El rótulo bajo COMUNÍQUESE siempre es el del abogado especializado asignado
    // (aunque el visto bueno lo diera un supervisor). Solo aparece tras el visto bueno.
    revisor: vistoBuenoDado ? buscar(decision?.revisor_abogado_id) : null,
    director: aprobado ? buscar(decision?.aprobada_por) : null,
  };
}

/** Trae la imagen de firma por el proxy autenticado; transparente si no hay. */
async function firmaBytes(firmaKey?: string | null): Promise<Uint8Array> {
  if (!firmaKey) return PNG_TRANSPARENTE;
  try {
    const blob = await descargarBlob(`/documentos/descargar?path=${encodeURIComponent(firmaKey)}`);
    return new Uint8Array(await blob.arrayBuffer());
  } catch {
    return PNG_TRANSPARENTE;
  }
}

/**
 * Renderiza el auto con el módulo de imágenes (docxtemplater-image-module-free). Los
 * valores de los tags de imagen son STRINGS (el nombre del tag); getImage los mapea a
 * los bytes reales (regla del módulo: un valor-objeto lo trata como ya resuelto y rompe).
 */
async function renderAuto(
  datos: DatosAutoInhibitorio,
  decision: DecisionInicialResumen | null | undefined,
  firmantes: Firmante[],
) {
  const { elaboro, revisor, director } = firmantesAuto(decision, firmantes);
  const [contenido, bRevisor, bDirector, bElaboro] = await Promise.all([
    cargarPlantilla(PLANTILLAS.AUTO_INHIBITORIO.ruta),
    firmaBytes(revisor?.firmaKey),
    firmaBytes(director?.firmaKey),
    firmaBytes(elaboro?.firmaKey),
  ]);
  const bytesPorTag: Record<string, Uint8Array> = {
    FIRMA_REVISOR: bRevisor,
    FIRMA_DIRECTOR: bDirector,
    FIRMA_ELABORO: bElaboro,
  };
  const [{ default: PizZip }, { default: Docxtemplater }, { default: ImageModule }] = await Promise.all([
    import('pizzip'),
    import('docxtemplater'),
    import('docxtemplater-image-module-free'),
  ]);
  const imageModule = new ImageModule({
    getImage: (tagValue: string) => bytesPorTag[tagValue] ?? PNG_TRANSPARENTE,
    getSize: () => [150, 45] as [number, number],
    centered: false,
  });
  const doc = new Docxtemplater(new PizZip(contenido), {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    modules: [imageModule as any],
    paragraphLoop: true,
    linebreaks: true,
    delimiters: { start: '{', end: '}' },
    nullGetter: () => '',
  });
  doc.render({
    ...datos,
    REVISOR: revisor?.nombre ?? '',
    DIRECTOR: director?.nombre ?? '',
    ELABORO: elaboro?.nombre ?? '',
    ROL_ELABORO: elaboro ? labelRol(elaboro.rol) : '',
    // Tags de imagen: el valor es el NOMBRE del tag (string).
    FIRMA_REVISOR: 'FIRMA_REVISOR',
    FIRMA_DIRECTOR: 'FIRMA_DIRECTOR',
    FIRMA_ELABORO: 'FIRMA_ELABORO',
  });
  return doc.getZip();
}

/** Genera el .docx del auto (con firmas-imagen) como Blob (para previsualizar). */
export async function blobAutoInhibitorio(
  datos: DatosAutoInhibitorio,
  decision?: DecisionInicialResumen | null,
  firmantes: Firmante[] = [],
): Promise<Blob> {
  const zip = await renderAuto(datos, decision, firmantes);
  return zip.generate({ type: 'blob', mimeType: MIME_DOCX }) as Blob;
}

/** Descarga el Auto Inhibitorio en .docx con los datos y firmas indicados. */
export async function descargarAutoInhibitorio(
  radicado: string,
  datos: DatosAutoInhibitorio,
  decision?: DecisionInicialResumen | null,
  firmantes: Firmante[] = [],
): Promise<void> {
  const zip = await renderAuto(datos, decision, firmantes);
  const blob = zip.generate({ type: 'blob', mimeType: MIME_DOCX }) as Blob;
  const { saveAs } = await import('file-saver');
  saveAs(blob, `auto-inhibitorio-${radicado || 'documento'}.docx`);
}

export { PlantillaFaltanteError };
